/**
 * @file ShopCreatedChecklist.tsx
 * @description Экран итога «Магазин создан» на месте формы (phase19.28
 * этап А; uiux-platform-shops §3; PL7, PL8, PL9).
 *
 * Строки: магазин создан; адрес привязан (DNS готов по wildcard); хост в
 * NPM — по последней проверке поддомена (не проверялся / нет — «!» со
 * ссылкой на инструкцию во вкладке «Домены»); приглашение отправлено или
 * владелец назначен; стартовый сайт собран или «не собрался … [Собрать
 * ещё раз]»; напоминание — сайт закрыт до договора, реквизитов и
 * политики ПДн (PL9).
 */

import { hostToUnicode } from "@chaiberi/shared";
import { CircleAlert, CircleCheck, CircleX } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";

import type { CreateShopResult } from "../../../lib/api-platform-shops";
import { formatPlatformDate, siteStarterText } from "../../../lib/platform/shop-format";
import { Button } from "../../ui/button";
import { CopyButton } from "./CopyButton";
import { SiteStarterButton } from "./SiteStarterButton";
import { cn } from "@/lib/utils";

type Mark = "ok" | "warn" | "fail";

const MARK = {
  ok: { icon: CircleCheck, className: "text-[var(--status-success-fg,var(--success))]", sr: "Готово" },
  warn: { icon: CircleAlert, className: "text-[var(--status-attention-dot,var(--warning))]", sr: "Нужно сделать" },
  fail: { icon: CircleX, className: "text-[var(--status-error-fg,var(--destructive))]", sr: "Не получилось" },
} as const;

function Line({ mark, children }: { mark: Mark; children: ReactNode }) {
  const meta = MARK[mark];
  const Icon = meta.icon;
  return (
    <li className="type-body flex items-start gap-2">
      <Icon aria-hidden className={cn("mt-0.5 size-5 shrink-0", meta.className)} />
      <span className="sr-only">{meta.sr}: </span>
      <span className="min-w-0 break-words">{children}</span>
    </li>
  );
}

interface Props {
  result: CreateShopResult;
  ownerMode: "existing" | "new" | null;
  onCreateMore: () => void;
}

export function ShopCreatedChecklist({ result, ownerMode, onCreateMore }: Props) {
  const { shop, invitation } = result;
  const base = `/platform/shops/${shop.id}`;
  const host = shop.primaryHostUnicode ?? (shop.primaryHost ? hostToUnicode(shop.primaryHost) : null);
  const subdomain = shop.domains.find((d) => d.isPrimary) ?? shop.domains[0];
  const proxyOk = subdomain?.lastCheck?.proxy?.ok === true;
  const owner = shop.owners[0];
  // «Собрать ещё раз» обновляет строку на месте (ревью М7).
  const [starterResult, setStarterResult] = useState(result.siteStarter ?? null);
  const starter = starterResult ? siteStarterText(starterResult) : null;

  return (
    <section aria-label="Итог создания магазина" className="flex flex-col gap-4" aria-live="polite">
      <ul className="flex flex-col gap-3">
        <Line mark="ok">Магазин «{shop.displayName}» создан</Line>
        {host ? <Line mark="ok">Адрес <span className="break-all">{host}</span> привязан · DNS готов (wildcard)</Line> : null}
        {proxyOk ? (
          <Line mark="ok">Хост в NPM отвечает</Line>
        ) : (
          <Line mark="warn">
            Хост в NPM ещё не заведён
            <Link to={`${base}/domains`} className="flex min-h-12 items-center font-medium text-primary underline-offset-4 hover:underline">
              Открыть инструкцию →
            </Link>
          </Line>
        )}
        {invitation && invitation.delivered === false ? (
          <Line mark="fail">
            Магазин создан, но письмо не отправлено: почтовый сервер не ответил. Скопируйте ссылку и передайте владельцу
            ({invitation.sentTo}, действует до {formatPlatformDate(invitation.expiresAt)})
            {invitation.url ? (
              <span className="mt-1 flex">
                <CopyButton value={invitation.url} label="Копировать ссылку" withText />
              </span>
            ) : null}
          </Line>
        ) : invitation ? (
          <Line mark="ok">
            Приглашение отправлено {invitation.sentTo}, действует до {formatPlatformDate(invitation.expiresAt)}
          </Line>
        ) : ownerMode === "new" ? (
          <Line mark="fail">
            Магазин создан, но письмо не отправлено: почтовый сервер не ответил. Отправьте приглашение ещё раз во вкладке «Доступ»
          </Line>
        ) : owner ? (
          <Line mark="ok">{owner.displayName || owner.email} получил роль «Владелец магазина»</Line>
        ) : null}
        {starter ? (
          <Line mark={starter.ok ? "ok" : "fail"}>
            {starter.text}
            {!starter.ok ? (
              <span className="mt-1 flex">
                <SiteStarterButton shopId={shop.id} shopName={shop.displayName} onApplied={setStarterResult} />
              </span>
            ) : null}
          </Line>
        ) : null}
        <Line mark="warn">Сайт закрыт до договора с платформой, реквизитов продавца и опубликованной политики ПДн</Line>
      </ul>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button asChild>
          <Link to={base}>Открыть карточку магазина</Link>
        </Button>
        <Button variant="outline" onClick={onCreateMore}>
          Создать ещё
        </Button>
      </div>
    </section>
  );
}
